import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Tile,
  Button,
  Tag,
  Select,
  SelectItem,
  Modal,
  Checkbox,
  InlineNotification,
} from '@carbon/react';
import {
  ArrowLeft,
  Temperature,
  Humidity,
  Location,
  Time,
  ArrowUp,
  ArrowDown,
  WarningAlt,
  Checkmark,
  Devices,
  Add,
  Download,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useZones } from '../hooks/useZones';
import { useSensorsOptimized } from '../hooks/useSensorsOptimized';
import { useZoneReadings } from '../hooks/useZoneReadings';
import { supabase } from '../services/supabaseClient';
import SensorChart from '../components/charts/SensorChart';
import ZoneMapModal from '../components/ZoneMapModal';
import './ZoneDetailPage.scss';

const ZoneDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { zones, isLoading: zonesLoading } = useZones();
  const { sensors: allSensors } = useSensorsOptimized();
  const { hasActiveSubscription, loading: subLoading } = useSubscription();
  const isDark = theme === 'dark';
  
  const [timeRange, setTimeRange] = useState('24h');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [assigningDevices, setAssigningDevices] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(false);

  // Find the zone by ID
  const zone = useMemo(() => {
    return zones.find(z => z.id === id) || null;
  }, [zones, id]);

  // Get sensors for this zone
  const zoneSensors = useMemo(() => {
    if (!zone) return [];
    return allSensors.filter(sensor => {
      // Check if sensor's zone_id matches, or if zone has sensor_ids array
      if (sensor.zone_id === zone.id) return true;
      if (zone.sensor_ids && Array.isArray(zone.sensor_ids)) {
        return zone.sensor_ids.includes(sensor.id);
      }
      return false;
    });
  }, [zone, allSensors]);

  // Get sensor IDs for the zone
  const sensorIds = useMemo(() => {
    return zoneSensors.map(s => s.id);
  }, [zoneSensors]);

  // Zone-average readings across all sensors in the zone.
  // Uses the same time-range vocabulary ('1h'|'6h'|'24h'|'7d'|'30d') as
  // DeviceDetailPage so the filter at the top selects the correct data window.
  const {
    readings: fullReadings,           // Zone-averaged series for CSV export
    downsampledReadings,              // Same density — ready for charts
    isLoading: loadingChart,
  } = useZoneReadings(sensorIds, timeRange);

  // Calculate zone averages
  const zoneAverages = useMemo(() => {
    if (zoneSensors.length === 0) {
      return {
        avgTemp: null,
        avgHumidity: null,
        tempMin: null,
        tempMax: null,
        humidityMin: null,
        humidityMax: null,
      };
    }

    let totalTemp = 0;
    let tempCount = 0;
    let totalHumidity = 0;
    let humidityCount = 0;
    const temps = [];
    const humidities = [];

    zoneSensors.forEach(sensor => {
      if (sensor.latest_reading) {
        if (sensor.latest_reading.temperature !== null && sensor.latest_reading.temperature !== undefined) {
          totalTemp += sensor.latest_reading.temperature;
          temps.push(sensor.latest_reading.temperature);
          tempCount++;
        }
        if (sensor.latest_reading.humidity !== null && sensor.latest_reading.humidity !== undefined && sensor.latest_reading.humidity > 0) {
          totalHumidity += sensor.latest_reading.humidity;
          humidities.push(sensor.latest_reading.humidity);
          humidityCount++;
        }
      }
    });

    const avgTemp = tempCount > 0 ? totalTemp / tempCount : null;
    const avgHumidity = humidityCount > 0 ? totalHumidity / humidityCount : null;
    const tempMin = temps.length > 0 ? Math.min(...temps) : null;
    const tempMax = temps.length > 0 ? Math.max(...temps) : null;
    const humidityMin = humidities.length > 0 ? Math.min(...humidities) : null;
    const humidityMax = humidities.length > 0 ? Math.max(...humidities) : null;

    return {
      avgTemp,
      avgHumidity,
      tempMin,
      tempMax,
      humidityMin,
      humidityMax,
    };
  }, [zoneSensors]);

  // Format last updated time - memoized to keep it stable
  const formatLastUpdated = useCallback(() => {
    if (zoneSensors.length === 0) return 'No data';
    
    let mostRecent = null;
    zoneSensors.forEach(sensor => {
      if (sensor.latest_reading && sensor.latest_reading.recorded_at) {
        const readingDate = new Date(sensor.latest_reading.recorded_at);
        if (!mostRecent || readingDate > mostRecent) {
          mostRecent = readingDate;
        }
      }
    });

    if (!mostRecent) return 'No data';

    const now = new Date();
    const diffMs = now - mostRecent;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffHours / 24)} day${Math.floor(diffHours / 24) > 1 ? 's' : ''} ago`;
  }, [zoneSensors]);

  // Determine zone status - memoized to keep it stable
  const getZoneStatus = useCallback(() => {
    if (zoneSensors.length === 0) return 'offline';
    
    let hasOnline = false;
    let hasWarning = false;
    
    zoneSensors.forEach(sensor => {
      if (sensor.latest_reading) {
        const readingDate = new Date(sensor.latest_reading.recorded_at);
        const now = new Date();
        const diffMins = Math.floor((now - readingDate) / 60000);
        
        if (diffMins <= 15) {
          hasOnline = true;
        } else if (diffMins <= 60) {
          hasWarning = true;
        }
      }
    });

    if (hasOnline) return 'normal';
    if (hasWarning) return 'warning';
    return 'offline';
  }, [zoneSensors]);

  // Format temperature
  const formatTemperature = (temp, unit = 'celsius') => {
    if (temp === null || temp === undefined) return '--.-°C';
    if (unit === 'fahrenheit') {
      const fahrenheit = (temp * 9/5) + 32;
      return `${fahrenheit.toFixed(1)}°F`;
    }
    return `${temp.toFixed(1)}°C`;
  };

  // Get temperature unit from sensors
  const tempUnit = zoneSensors.length > 0 && zoneSensors[0].temperature_unit ? zoneSensors[0].temperature_unit : 'celsius';

  // Convert zone-averaged readings to chart data format (same shape as
  // DeviceDetailPage: { group, date, dateTime, value }) for SensorChart.
  const { tempChartData, humidityChartData } = useMemo(() => {
    if (!downsampledReadings || downsampledReadings.length === 0) {
      return { tempChartData: [], humidityChartData: [] };
    }

    const tempData = [];
    const humidityData = [];

    downsampledReadings.forEach(reading => {
      let temp = reading.temperature;
      if (tempUnit === 'fahrenheit' && temp != null) {
        temp = (temp * 9 / 5) + 32;
      }
      const humidity = (reading.humidity != null && reading.humidity > 0) ? reading.humidity : null;
      const date = new Date(reading.created_at || reading.recorded_at || reading.timestamp);
      if (isNaN(date.getTime())) return;

      const dateTimeString = date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
        date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

      if (temp != null) {
        tempData.push({
          group: 'Zone Average',
          date: date.toISOString(),
          dateTime: dateTimeString,
          value: temp,
        });
      }
      if (humidity != null) {
        humidityData.push({
          group: 'Zone Average',
          date: date.toISOString(),
          dateTime: dateTimeString,
          value: humidity,
        });
      }
    });

    return { tempChartData: tempData, humidityChartData: humidityData };
  }, [downsampledReadings, tempUnit]);

  // Format zone data for display
  const zoneData = useMemo(() => {
    if (!zone) {
      return {
        id: id || '1',
        name: 'Loading...',
        description: '',
        status: 'offline',
        deviceCount: 0,
        lastUpdated: 'No data',
        avgTemp: null,
        avgHumidity: null,
        tempMin: null,
        tempMax: null,
        humidityMin: null,
        humidityMax: null,
        devices: [],
      };
    }

    return {
      id: zone.id,
      name: zone.name,
      description: zone.description || '',
      status: getZoneStatus(),
      deviceCount: zoneSensors.length,
      lastUpdated: formatLastUpdated(),
      avgTemp: zoneAverages.avgTemp,
      avgHumidity: zoneAverages.avgHumidity,
      tempMin: zoneAverages.tempMin,
      tempMax: zoneAverages.tempMax,
      humidityMin: zoneAverages.humidityMin,
      humidityMax: zoneAverages.humidityMax,
      devices: zoneSensors.map(sensor => ({
        id: sensor.id,
        name: sensor.name,
        temp: sensor.latest_reading ? sensor.latest_reading.temperature : null,
        humidity: sensor.latest_reading ? sensor.latest_reading.humidity : null,
        status: sensor.latest_reading ? (() => {
          const readingDate = new Date(sensor.latest_reading.recorded_at);
          const now = new Date();
          const diffMins = Math.floor((now - readingDate) / 60000);
          if (diffMins > 60) return 'offline';
          if (diffMins > 15) return 'warning';
          return 'online';
        })() : 'offline',
      })),
    };
  }, [zone, zoneSensors, zoneAverages, id, getZoneStatus, formatLastUpdated]);

  // Calculate trend from readings (matching SensorPush app logic)
  // Works with both zone aggregated data (Carbon format) and raw readings
  const calculateTrendFromReadings = useCallback((readings, type = 'temperature') => {
    if (!readings || readings.length < 2) {
      return { trend: 'STEADY', rate: null, hasData: false };
    }

    // Sort by date/timestamp (oldest first)
    const sortedReadings = [...readings].sort((a, b) => {
      const dateA = new Date(a.date || a.recorded_at || a.timestamp);
      const dateB = new Date(b.date || b.recorded_at || b.timestamp);
      return dateA - dateB;
    });

    // Get first and last readings
    const first = sortedReadings[0];
    const last = sortedReadings[sortedReadings.length - 1];

    // Get values
    const firstValue = first.value !== undefined ? first.value : (type === 'temperature' ? first.temperature : first.humidity);
    const lastValue = last.value !== undefined ? last.value : (type === 'temperature' ? last.temperature : last.humidity);

    if (firstValue === null || lastValue === null || firstValue === undefined || lastValue === undefined) {
      return { trend: 'STEADY', rate: null, hasData: false };
    }

    // Calculate time difference
    const firstTime = new Date(first.date || first.recorded_at || first.timestamp).getTime();
    const lastTime = new Date(last.date || last.recorded_at || last.timestamp).getTime();
    const timeDiffMs = lastTime - firstTime;

    if (timeDiffMs <= 0) {
      return { trend: 'STEADY', rate: null, hasData: true };
    }

    // Calculate rate of change per hour
    const hoursDiff = timeDiffMs / (1000 * 60 * 60);
    const valueChange = lastValue - firstValue;
    const ratePerHour = valueChange / hoursDiff;

    // Define thresholds (matching SensorPush app)
    const tempThreshold = 0.5; // 0.5°C per hour
    const humidityThreshold = 2; // 2% per hour
    const threshold = type === 'temperature' ? tempThreshold : humidityThreshold;

    // Determine trend
    let trend = 'STEADY';
    if (Math.abs(ratePerHour) >= threshold) {
      trend = ratePerHour > 0 ? 'RISING' : 'FALLING';
    }

    return {
      trend,
      rate: ratePerHour,
      hasData: true,
    };
  }, []);

  // Calculate zone trend from Carbon chart data
  const calculateZoneTrendFromChartData = useCallback((chartData, type = 'temperature') => {
    if (!chartData || chartData.length === 0) {
      return { direction: 'stable', rate: null, hasData: false, trendText: 'No data' };
    }

    // Filter to get only 'Zone Average' data points (or the main data if not aggregated)
    const zoneAverageData = chartData.filter(d => d.group === 'Zone Average' || !chartData.some(x => x.group === 'Zone Average'));
    
    if (zoneAverageData.length < 2) {
      // Try with all data if Zone Average doesn't have enough points
      if (chartData.length >= 2) {
        const { trend, rate, hasData } = calculateTrendFromReadings(chartData, type);
        const direction = trend === 'RISING' ? 'up' : trend === 'FALLING' ? 'down' : 'stable';
        const unit = type === 'temperature' ? '°C/hr' : '%/hr';
        const rateDisplay = hasData && rate !== null ? `${rate > 0 ? '+' : ''}${rate.toFixed(2)}${unit}` : `--.--${unit}`;
        const trendText = trend === 'RISING' ? 'Rising' : trend === 'FALLING' ? 'Falling' : 'Stable';
        
        return { direction, rate: rate?.toFixed(2) || null, hasData, trendText, rateDisplay };
      }
      return { direction: 'stable', rate: null, hasData: false, trendText: 'Insufficient data' };
    }

    const { trend, rate, hasData } = calculateTrendFromReadings(zoneAverageData, type);
    
    const direction = trend === 'RISING' ? 'up' : trend === 'FALLING' ? 'down' : 'stable';
    const unit = type === 'temperature' ? '°C/hr' : '%/hr';
    const rateDisplay = hasData && rate !== null ? `${rate > 0 ? '+' : ''}${rate.toFixed(2)}${unit}` : `--.--${unit}`;
    const trendText = trend === 'RISING' ? 'Rising' : trend === 'FALLING' ? 'Falling' : 'Stable';

    return {
      direction,
      rate: rate?.toFixed(2) || null,
      hasData,
      trendText,
      rateDisplay,
    };
  }, [calculateTrendFromReadings]);

  // Get available devices (not assigned to this zone)
  const availableDevices = useMemo(() => {
    const zoneSensorIds = zoneSensors.map(s => s.id);
    return allSensors.filter(sensor => !zoneSensorIds.includes(sensor.id));
  }, [allSensors, zoneSensors]);

  // Handle device selection toggle
  const handleDeviceToggle = (deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  // Handle assign devices
  const handleAssignDevices = async () => {
    if (selectedDevices.length === 0) return;
    
    setAssigningDevices(true);
    setAssignError(null);
    setAssignSuccess(false);

    try {
      // Update each selected sensor with the zone_id
      const updates = selectedDevices.map(deviceId =>
        supabase
          .from('sensors')
          .update({ zone_id: zone.id })
          .eq('id', deviceId)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(result => result.error);
      if (errors.length > 0) {
        throw new Error(`Failed to assign ${errors.length} device(s)`);
      }

      // Success
      setAssignSuccess(true);
      setSelectedDevices([]);
      
      // Close modal after 1.5 seconds
      setTimeout(() => {
        setIsModalOpen(false);
        setAssignSuccess(false);
        // Refresh the page to show updated devices
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error assigning devices:', error);
      setAssignError(error.message || 'Failed to assign devices. Please try again.');
    } finally {
      setAssigningDevices(false);
    }
  };

  // Handle modal open
  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSelectedDevices([]);
    setAssignError(null);
    setAssignSuccess(false);
  };

  // Handle map modal close - memoized to prevent re-renders
  const handleCloseMapModal = useCallback(() => {
    setIsMapModalOpen(false);
  }, []);

  // Calculate trends from zone-average chart data
  const tempTrend = useMemo(() =>
    calculateZoneTrendFromChartData(tempChartData, 'temperature'),
    [tempChartData, calculateZoneTrendFromChartData]
  );

  const humidityTrend = useMemo(() =>
    calculateZoneTrendFromChartData(humidityChartData, 'humidity'),
    [humidityChartData, calculateZoneTrendFromChartData]
  );

  // Chart colors — single line per chart, matching DeviceDetailPage
  const chartColors = {
    temperature: '#0f62fe',
    humidity: '#24a148',
    grid: isDark ? '#393939' : '#e0e0e0',
    text: isDark ? '#c6c6c6' : '#525252',
    bg: isDark ? '#262626' : '#f4f4f4',
  };

  // CSV export — uses zone-averaged readings (full series), mirroring
  // DeviceDetailPage's handleCSVExport.
  const handleCSVExport = useCallback((dataType, zoneName) => {
    if (!fullReadings || fullReadings.length === 0) return;

    const csvData = fullReadings.map(reading => {
      const date = new Date(reading.created_at || reading.recorded_at || reading.timestamp);
      if (isNaN(date.getTime())) return null;
      const dateTime = date.toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' +
        date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

      let value = null;
      if (dataType === 'Temperature') {
        value = reading.temperature;
        if (tempUnit === 'fahrenheit' && value != null) value = (value * 9 / 5) + 32;
      } else if (dataType === 'Humidity') {
        value = reading.humidity;
      }
      return {
        'Date & Time': dateTime,
        [dataType]: value != null ? value.toFixed(2) : '',
      };
    }).filter(Boolean);

    if (csvData.length === 0) return;

    const headers = Object.keys(csvData[0]);
    const csvRows = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ];

    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const sanitizedName = (zoneName || 'Zone').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const stamp = new Date().toISOString().split('T')[0];
    link.setAttribute('download', `${sanitizedName}_${dataType.toLowerCase()}_${stamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [fullReadings, tempUnit]);

  if (zonesLoading) {
    return (
      <div className={`zone-detail ${isDark ? 'zone-detail--dark' : 'zone-detail--light'}`}>
        <div className="zone-detail__container">
          <div style={{ padding: '40px', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
            Loading zone details...
          </div>
        </div>
      </div>
    );
  }

  if (!zone) {
    return (
      <div className={`zone-detail ${isDark ? 'zone-detail--dark' : 'zone-detail--light'}`}>
        <div className="zone-detail__container">
          <div className="zone-detail__header">
            <Button kind="ghost" size="sm" renderIcon={ArrowLeft} onClick={() => navigate('/dashboard/zones')} className="zone-detail__back-btn">
              Back
            </Button>
            <div className="zone-detail__header-content">
              <h1 className="zone-detail__title">Zone Not Found</h1>
            </div>
          </div>
          <div style={{ padding: '40px', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
            <p>The zone you're looking for doesn't exist or you don't have access to it.</p>
            <Button kind="primary" onClick={() => navigate('/dashboard/zones')} style={{ marginTop: '16px' }}>
              Back to Zones
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      feature="zone details and monitoring data"
      disabled={!hasActiveSubscription}
      showChildrenGreyed={true}
      customMessage="You need an active subscription to access zone details and monitoring data. Your zone configuration is preserved and will be accessible once you resubscribe."
    >
    <div className={`zone-detail ${isDark ? 'zone-detail--dark' : 'zone-detail--light'}`}>
      <div className="zone-detail__container">
        {/* Header */}
        <div className="zone-detail__header">
          <Button
            kind="ghost"
            size="sm"
            renderIcon={ArrowLeft}
            onClick={() => navigate('/dashboard/zones')}
            className="zone-detail__back-btn"
          >
            Back
          </Button>
          <div className="zone-detail__header-content">
            <h1 className="zone-detail__title">{zoneData.name}</h1>
            <p className="zone-detail__description">{zoneData.description}</p>
            <div className="zone-detail__meta">
              <Tag type={zoneData.status === 'normal' ? 'green' : zoneData.status === 'warning' ? 'orange' : 'red'} size="sm">
                {zoneData.status.toUpperCase()}
              </Tag>
              <span className="zone-detail__devices">
                <Devices size={14} /> {zoneData.deviceCount} device{zoneData.deviceCount !== 1 ? 's' : ''}
              </span>
              <span className="zone-detail__last-updated">
                <Time size={14} /> {zoneData.lastUpdated}
              </span>
            </div>
            {/* View on Map Button */}
            <Button
              kind="tertiary"
              size="sm"
              renderIcon={Location}
              onClick={() => setIsMapModalOpen(true)}
              style={{ marginTop: '12px' }}
              className="zone-detail__map-button"
            >
              View on Map
            </Button>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="zone-detail__time-selector">
          <Select
            id="timeRange"
            labelText=""
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            size="sm"
          >
            <SelectItem value="1h" text="Last 1 hour" />
            <SelectItem value="6h" text="Last 6 hours" />
            <SelectItem value="24h" text="Last 24 hours" />
            <SelectItem value="7d" text="Last 7 days" />
            <SelectItem value="30d" text="Last 30 days" />
          </Select>
        </div>

        {/* Zone Averages */}
        <div className="zone-detail__averages">
          <Tile className="zone-detail__average-card">
            <div className="zone-detail__average-icon" style={{ backgroundColor: '#0f62fe20', color: '#0f62fe' }}>
              <Temperature size={24} />
            </div>
            <div className="zone-detail__average-info">
              <span className="zone-detail__average-label">Avg Temperature</span>
              <span className="zone-detail__average-value">{zoneData.avgTemp !== null ? formatTemperature(zoneData.avgTemp, tempUnit) : '--.-°C'}</span>
              <span className={`zone-detail__average-status ${zoneData.avgTemp !== null && zoneData.tempMin !== null && zoneData.tempMax !== null && zoneData.avgTemp >= zoneData.tempMin && zoneData.avgTemp <= zoneData.tempMax ? 'zone-detail__average-status--ok' : 'zone-detail__average-status--alert'}`}>
                {zoneData.avgTemp !== null && zoneData.tempMin !== null && zoneData.tempMax !== null && zoneData.avgTemp >= zoneData.tempMin && zoneData.avgTemp <= zoneData.tempMax ? (
                  <><Checkmark size={14} /> Within range</>
                ) : zoneData.avgTemp !== null ? (
                  <><WarningAlt size={14} /> Out of range</>
                ) : (
                  <><WarningAlt size={14} /> No data</>
                )}
              </span>
            </div>
          </Tile>
          
          <Tile className="zone-detail__average-card">
            <div className="zone-detail__average-icon" style={{ backgroundColor: '#24a14820', color: '#24a148' }}>
              <Humidity size={24} />
            </div>
            <div className="zone-detail__average-info">
              <span className="zone-detail__average-label">Avg Humidity</span>
              <span className="zone-detail__average-value">{zoneData.avgHumidity !== null && zoneData.avgHumidity > 0 ? `${zoneData.avgHumidity.toFixed(1)}%` : 'No data'}</span>
              <span className={`zone-detail__average-status ${zoneData.avgHumidity !== null && zoneData.humidityMin !== null && zoneData.humidityMax !== null && zoneData.avgHumidity >= zoneData.humidityMin && zoneData.avgHumidity <= zoneData.humidityMax ? 'zone-detail__average-status--ok' : 'zone-detail__average-status--alert'}`}>
                {zoneData.avgHumidity !== null && zoneData.humidityMin !== null && zoneData.humidityMax !== null && zoneData.avgHumidity >= zoneData.humidityMin && zoneData.avgHumidity <= zoneData.humidityMax ? (
                  <><Checkmark size={14} /> Within range</>
                ) : zoneData.avgHumidity !== null ? (
                  <><WarningAlt size={14} /> Out of range</>
                ) : (
                  <><WarningAlt size={14} /> No data</>
                )}
              </span>
            </div>
          </Tile>
        </div>

        {/* Trends Card */}
        <Tile className="zone-detail__trends-card">
          <h3 className="zone-detail__section-title">Zone Trends</h3>
          <div className="zone-detail__trends">
            <div className="zone-detail__trend-item">
              <div className="zone-detail__trend-header">
                <Temperature size={18} />
                <span>Temperature Trend</span>
              </div>
              <div className={`zone-detail__trend-value zone-detail__trend-value--${tempTrend.direction}`}>
                {tempTrend.direction === 'up' ? <ArrowUp size={20} /> : tempTrend.direction === 'down' ? <ArrowDown size={20} /> : null}
                <span>
                  {tempTrend.rateDisplay || '--.--°C/hr'}
                </span>
                <span className="zone-detail__trend-label">
                  {tempTrend.trendText || 'Stable'}
                </span>
              </div>
            </div>
            <div className="zone-detail__trend-item">
              <div className="zone-detail__trend-header">
                <Humidity size={18} />
                <span>Humidity Trend</span>
              </div>
              <div className={`zone-detail__trend-value zone-detail__trend-value--${humidityTrend.direction}`}>
                {humidityTrend.direction === 'up' ? <ArrowUp size={20} /> : humidityTrend.direction === 'down' ? <ArrowDown size={20} /> : null}
                <span>
                  {humidityTrend.rateDisplay || '--.--%/hr'}
                </span>
                <span className="zone-detail__trend-label">
                  {humidityTrend.trendText || 'Stable'}
                </span>
              </div>
            </div>
          </div>
        </Tile>

        {/* Temperature Chart — Zone Average */}
        <Tile className="zone-detail__chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="zone-detail__section-title" style={{ margin: 0 }}>
              Temperature ({tempUnit === 'fahrenheit' ? '°F' : '°C'})
            </h3>
            {tempChartData.length > 0 && (
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Download}
                onClick={() => handleCSVExport('Temperature', zone?.name)}
                iconDescription="Export CSV"
                hasIconOnly
                tooltipPosition="left"
                tooltipAlignment="end"
              />
            )}
          </div>
          <div className="zone-detail__chart" style={{ position: 'relative', paddingBottom: '8px' }}>
            <SensorChart
              data={tempChartData}
              label="Temperature"
              unit={tempUnit === 'fahrenheit' ? '°F' : '°C'}
              color={chartColors.temperature}
              timeRange={timeRange}
              isDark={isDark}
              loading={loadingChart}
              onExport={(dataType) => handleCSVExport(dataType, zone?.name)}
            />
          </div>
        </Tile>

        {/* Humidity Chart — Zone Average */}
        <Tile className="zone-detail__chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="zone-detail__section-title" style={{ margin: 0 }}>
              Humidity (%)
            </h3>
            {humidityChartData.length > 0 && (
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Download}
                onClick={() => handleCSVExport('Humidity', zone?.name)}
                iconDescription="Export CSV"
                hasIconOnly
                tooltipPosition="left"
                tooltipAlignment="end"
              />
            )}
          </div>
          <div className="zone-detail__chart" style={{ position: 'relative', paddingBottom: '8px' }}>
            <SensorChart
              data={humidityChartData}
              label="Humidity"
              unit="%"
              color={chartColors.humidity}
              timeRange={timeRange}
              isDark={isDark}
              loading={loadingChart}
              onExport={(dataType) => handleCSVExport(dataType, zone?.name)}
            />
          </div>
        </Tile>

        {/* Add Device Modal */}
        <Modal
          open={isModalOpen}
          onRequestClose={() => setIsModalOpen(false)}
          modalHeading="Assign Devices to Zone"
          modalLabel={zone?.name}
          primaryButtonText="Assign Selected"
          secondaryButtonText="Cancel"
          onRequestSubmit={handleAssignDevices}
          primaryButtonDisabled={selectedDevices.length === 0 || assigningDevices}
          size="sm"
        >
          {assignSuccess && (
            <InlineNotification
              kind="success"
              title="Success!"
              subtitle={`${selectedDevices.length} device(s) assigned to zone`}
              lowContrast
              hideCloseButton
              style={{ marginBottom: '1rem' }}
            />
          )}
          
          {assignError && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={assignError}
              lowContrast
              onClose={() => setAssignError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {availableDevices.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
              <p>No devices available to assign</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                All your devices are already assigned to zones or you haven't added any devices yet.
              </p>
            </div>
          ) : (
            <div>
              <p style={{ marginBottom: '1rem', color: isDark ? '#c6c6c6' : '#525252' }}>
                Select one or more devices to assign to this zone:
              </p>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {availableDevices.map(device => (
                  <div
                    key={device.id}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      backgroundColor: isDark ? '#262626' : '#f4f4f4',
                      border: `1px solid ${isDark ? '#393939' : '#e0e0e0'}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => handleDeviceToggle(device.id)}
                  >
                    <Checkbox
                      id={`device-${device.id}`}
                      labelText={device.name}
                      checked={selectedDevices.includes(device.id)}
                      onChange={() => handleDeviceToggle(device.id)}
                    />
                    <div style={{ 
                      marginLeft: '28px', 
                      marginTop: '4px', 
                      fontSize: '0.75rem', 
                      color: isDark ? '#a8a8a8' : '#6f6f6f' 
                    }}>
                      {device.device_id && `ID: ${device.device_id.slice(-8)}`}
                      {device.latest_reading && device.latest_reading.temperature !== null && (
                        <span style={{ marginLeft: '12px' }}>
                          <Temperature size={12} /> {formatTemperature(device.latest_reading.temperature, tempUnit)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: isDark ? '#a8a8a8' : '#6f6f6f' }}>
                {selectedDevices.length} device(s) selected
              </p>
            </div>
          )}
        </Modal>

        {/* Devices in Zone */}
        <Tile className="zone-detail__devices-card">
          <div className="zone-detail__devices-header">
            <h3 className="zone-detail__section-title">Devices in Zone</h3>
            <Button kind="ghost" size="sm" renderIcon={Add} onClick={handleOpenModal}>
              Add Device
            </Button>
          </div>
          <div className="zone-detail__devices-list">
            {zoneData.devices.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: isDark ? '#c6c6c6' : '#525252' }}>
                <p>No devices assigned to this zone</p>
                <Button kind="tertiary" size="sm" onClick={() => navigate('/dashboard/devices/add')} style={{ marginTop: '12px' }}>
                  Add Device
                </Button>
              </div>
            ) : (
              zoneData.devices.map((device) => (
                <div 
                  key={device.id} 
                  className="zone-detail__device-item"
                  onClick={() => navigate(`/dashboard/devices/${device.id}`)}
                >
                  <div className="zone-detail__device-info">
                    <span className="zone-detail__device-name">{device.name}</span>
                    <Tag type={device.status === 'online' ? 'green' : device.status === 'warning' ? 'orange' : 'gray'} size="sm">
                      {device.status}
                    </Tag>
                  </div>
                  <div className="zone-detail__device-readings">
                    <span><Temperature size={14} /> {device.temp !== null ? formatTemperature(device.temp, tempUnit) : '--.-°C'}</span>
                    <span><Humidity size={14} /> {device.humidity !== null && device.humidity > 0 ? `${device.humidity.toFixed(1)}%` : 'No data'}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Tile>

        {/* Zone Settings */}
        <Tile className="zone-detail__settings-card">
          <h3 className="zone-detail__section-title">Zone Settings</h3>
          <div className="zone-detail__settings">
            <div className="zone-detail__setting-row">
              <span>Temperature Range</span>
              <span>
                {zoneData.tempMin !== null && zoneData.tempMax !== null 
                  ? `${formatTemperature(zoneData.tempMin, tempUnit)} to ${formatTemperature(zoneData.tempMax, tempUnit)}`
                  : 'Not set'}
              </span>
            </div>
            <div className="zone-detail__setting-row">
              <span>Humidity Range</span>
              <span>
                {zoneData.humidityMin !== null && zoneData.humidityMax !== null && zoneData.humidityMin > 0 && zoneData.humidityMax > 0
                  ? `${zoneData.humidityMin.toFixed(1)}% to ${zoneData.humidityMax.toFixed(1)}%`
                  : 'Not set'}
              </span>
            </div>
          </div>
          <Button kind="tertiary" size="sm" style={{ marginTop: '1rem' }}>
            Edit Zone Settings
          </Button>
        </Tile>
      </div>

      {/* Zone Map Modal */}
      <ZoneMapModal
        isOpen={isMapModalOpen}
        onClose={handleCloseMapModal}
        zone={zone}
        sensors={zoneSensors}
      />
    </div>
    </SubscriptionGate>
  );
};

export default ZoneDetailPage;
